import { Controller } from "@hotwired/stimulus"
import { observeViewportEntry } from "../../../javascript/lib/viewport_midpoint.js"

export default class extends Controller {
  static targets = ["step", "stageEnd", "control", "query"]
  static values = { query: String }

  connect() {
    this.index = this.element.dataset.flowComplete ? this.stepTargets.length : -1
    this.typed = 0
    this.started = this.index !== -1
    this.paused = false
    this.userEdited = this.queryTarget.value !== this.queryValue
    this.visible = new Set()
    this.motion = window.matchMedia("(prefers-reduced-motion: reduce)")
    this.sync = this.sync.bind(this)
    this.motionChanged = this.motionChanged.bind(this)
    this.motion.addEventListener("change", this.motionChanged)
    document.addEventListener("visibilitychange", this.sync)
    this.observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.35) this.visible.add(entry.target)
        else this.visible.delete(entry.target)
      })
      this.sync()
    }, { threshold: [0, 0.35] })
    this.stepTargets.forEach(step => {
      step.dataset.flowState = this.index === -1 ? "pending" : "complete"
      this.observer.observe(step)
    })
    this.observer.observe(this.queryTarget)
    this.stopWatchingStart = observeViewportEntry(this.element, () => {
      this.started = true
      this.sync()
    })
    if (this.index === -1 && !this.motion.matches) this.queryTarget.value = ""
    this.motionChanged()
    this.sync()
  }

  sync() {
    const target = this.index === -1 ? this.queryTarget : this.stepTargets[this.index]
    const running = this.started && !this.paused && !document.hidden && this.visible.has(target)
    this.element.dataset.animationPaused = String(!running)
    this.controlTarget.textContent = !target ? "Replay animation" : !this.started || this.paused ? "Play animation" : "Pause animation"
    if (!running) {
      clearTimeout(this.timer)
      this.timer = null
      return
    }
    if (this.index === -1) {
      if (!this.timer) this.timer = setTimeout(() => {
        this.timer = null
        this.typed += 1
        this.queryTarget.value = this.queryValue.slice(0, this.typed)
        if (this.typed === this.queryValue.length) this.index = 0
        this.sync()
      }, 75)
    } else {
      target.dataset.flowState = "animating"
    }
  }

  complete(event) {
    if (event.animationName !== "teams-query-reveal" || event.target !== this.stageEndTargets[this.index]) return
    const step = this.stepTargets[this.index]
    step.dataset.flowState = "complete"
    this.index += 1
    if (this.index === this.stepTargets.length) this.finish()
    else this.sync()
  }

  toggle() {
    if (!this.started) {
      this.started = true
      this.stopWatchingStart()
    } else if (this.index === this.stepTargets.length) {
      this.index = -1
      this.typed = 0
      this.queryTarget.value = ""
      this.userEdited = false
      this.paused = false
      delete this.element.dataset.flowComplete
      this.stepTargets.forEach(step => { step.dataset.flowState = "pending" })
    } else {
      this.paused = !this.paused
    }
    this.sync()
  }

  edit() {
    this.userEdited = true
    this.finish()
  }

  motionChanged() {
    this.controlTarget.hidden = this.motion.matches
    if (this.motion.matches) this.finish()
  }

  finish() {
    this.stopWatchingStart()
    this.started = true
    clearTimeout(this.timer)
    this.timer = null
    if (!this.userEdited) this.queryTarget.value = this.queryValue
    this.index = this.stepTargets.length
    this.element.dataset.flowComplete = "true"
    this.stepTargets.forEach(step => { step.dataset.flowState = "complete" })
    this.controlTarget.textContent = "Replay animation"
  }

  disconnect() {
    this.observer.disconnect()
    this.motion.removeEventListener("change", this.motionChanged)
    document.removeEventListener("visibilitychange", this.sync)
    this.finish()
  }
}
